import {getLocalTimeZone, today} from '@internationalized/date'
import type {ButtonProps, DateValue, PopoverProps} from 'react-aria-components'
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  FieldError,
  Group,
  Heading,
  Label,
  Popover,
  Text,
} from 'react-aria-components'

import CalendarSvg from '@/assets/flight/icons/calendar.svg?react'
import ChevronLeftSvg from '@/assets/icons/chevron-left.svg?react'
import ChevronRightSvg from '@/assets/icons/chevron-right.svg?react'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './InnosearchDatePicker.module.scss'

interface Props {
  label: string
  date: DateValue | null
  setDate: MemoizedCallbackOrDispatch<DateValue | null>
  isDisabled?: boolean
  minValue?: DateValue | null
  errorMessage?: string
}

export default memo(function InnosearchDatePicker(props: Props) {
  return (
    <DatePicker
      className={style.DatePicker}
      value={props.date}
      onChange={props.setDate}
      isDisabled={props.isDisabled}
      minValue={props.minValue ?? today(getLocalTimeZone())}
      isRequired
      shouldForceLeadingZeros
      isInvalid={!!props.errorMessage}
    >
      <VisuallyHidden as={Label}>{props.label}</VisuallyHidden>
      <Group className={style.Group}>
        <Button className={style.Button}>
          <CalendarSvg />
        </Button>
        <DateInput className={style.DateInput}>
          {segment => <DateSegment segment={segment} className={style.DateSegment} />}
        </DateInput>
      </Group>
      <FieldError className={style.FieldError}>{props.errorMessage}</FieldError>
      <MyPopover>
        <Dialog className={style.Dialog}>
          <Calendar>
            <header className={style.Header}>
              <RoundButton slot='previous'>
                <ChevronLeftSvg />
              </RoundButton>
              <Heading className={style.Heading} />
              <RoundButton slot='next'>
                <ChevronRightSvg />
              </RoundButton>
            </header>
            <CalendarGrid className={style.CalendarGrid}>
              <CalendarGridHeader>
                {day => (
                  <CalendarHeaderCell className={style.CalendarHeaderCell}>
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {date => <CalendarCell date={date} className={style.CalendarCell} />}
              </CalendarGridBody>
            </CalendarGrid>
            <Text slot='errorMessage' />
          </Calendar>
        </Dialog>
      </MyPopover>
    </DatePicker>
  )
})

function RoundButton(props: ButtonProps) {
  return <Button {...props} className={style.RoundButton} />
}

function MyPopover(props: PopoverProps) {
  return <Popover {...props} className={style.Popover} offset={24} />
}
